/**
 * Event Registration Page
 * Public page for event registration via QR code/CTA links
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../services/supabase";
import {
  CalendarIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

interface EventDetails {
  id: string;
  title: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  location_name: string | null;
  location_address: any | null;
  status: string;
  capacity: number | null;
  current_attendee_count?: number;
}

export default function EventRegister() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event_id");
  const ctaId = searchParams.get("ctaid");
  // Phase 1.1 midstream review: utm_source / utm_medium were
  // captured here but only ever used in the broken event_attendees
  // insert (columns don't exist on that table). Dropped along with
  // the insert fix. The fields are not lost — they're still in the
  // URL and a future tracking column could pick them up.

  const [event, setEvent] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Registrations closed because a flagged event has hit its hard attendance cap (authoritative,
  // server-side via event_registration_open). Shown BEFORE the form so people don't fill it out.
  const [regClosed, setRegClosed] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Load event details
  useEffect(() => {
    async function loadEvent() {
      if (!eventId) {
        setError("No event specified");
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from("events")
          .select(
            `
            id,
            title,
            description,
            start_date,
            end_date,
            location_name,
            location_address,
            status,
            capacity
          `,
          )
          .eq("id", eventId)
          .single();

        if (fetchError) {
          throw fetchError;
        }
        if (!data) {
          setError("Event not found");
          setLoading(false);
          return;
        }

        // Aggregate endpoint, not a row read -- this page is PUBLIC, and anonymous callers no
        // longer hold SELECT on event_attendees (audit H1). `used_rows` preserves this page's
        // existing row-based semantics; the landing page counts PEOPLE. That inconsistency is
        // pre-existing and deliberately NOT changed inside a security fix.
        const { data: cap } = await supabase.rpc("public_event_capacity_used", {
          p_event_id: eventId,
        });

        setEvent({
          ...data,
          current_attendee_count:
            (cap as { used_rows?: number } | null)?.used_rows ?? 0,
        });

        // Authoritative "registrations open?" check — false when a hard-capped event is full.
        const { data: regOpen } = await supabase.rpc(
          "event_registration_open",
          {
            p_event_id: eventId,
          },
        );
        setRegClosed(regOpen === false);
      } catch (err) {
        console.error("Error loading event:", err);
        setError("Failed to load event details");
      } finally {
        setLoading(false);
      }
    }

    loadEvent();
  }, [eventId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!event) {
      return;
    }

    // Basic validation
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !phone.trim()
    ) {
      setError("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Boolean endpoint, not a row read (audit H1). Returns no attendee data.
      const { data: alreadyRegistered } = await supabase.rpc(
        "public_event_registration_exists",
        { p_event_id: event.id, p_email: email },
      );

      if (alreadyRegistered === true) {
        setError("This email is already registered for this event");
        setSubmitting(false);
        return;
      }

      // Check capacity
      if (event.capacity && event.current_attendee_count !== undefined) {
        if (event.current_attendee_count >= event.capacity) {
          setError("Sorry, this event has reached its capacity");
          setSubmitting(false);
          return;
        }
      }

      // Create attendee record
      // Phase 1.1 midstream review: this insert had three
      // pre-existing bugs that produced zero successful rows on PROD:
      //   - `phone_number` column doesn't exist (real col is `phone`)
      //   - `utm_source`/`utm_medium` columns don't exist
      //   - missing `attendee_type` (NOT NULL no-default)
      // Fixed alongside the cta_conversions removal so the same
      // class of silent-fail code disappears in one pass.
      const { error: insertError } = await supabase
        .from("event_attendees")
        .insert({
          event_id: event.id,
          attendee_type: "prospect",
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.toLowerCase().trim(),
          phone: phone.trim(),
          attendance_status: "registered",
          registration_source: "cta",
          cta_id: ctaId || null,
        });

      if (insertError) {
        throw insertError;
      }

      // Phase 1.1 midstream review (Bug 4): removed cta_conversions
      // insert. The `cta_conversions` table does not exist on QA or
      // PROD — every call silently failed via the .catch() swallow.
      // Rep attribution is now captured by event_attendees.cta_id
      // above. utm_source/utm_medium tracking is a follow-up; the
      // CTA URL still carries them and they could land in a future
      // tracking column.

      setSuccess(true);
    } catch (err) {
      console.error("Error registering:", err);
      // If the event filled between page load and submit, the hard-cap guard rejects the insert.
      const e = err as { message?: string; hint?: string };
      const capReached =
        e?.hint === "event_attendance_cap_reached" ||
        /attendance limit has been reached/i.test(String(e?.message || ""));
      if (capReached) {
        setRegClosed(true);
        setError(
          "The event attendance limit has been reached. Registrations are closed.",
        );
      } else {
        setError("Failed to complete registration. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Unable to Load Event
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Registration Complete!
          </h1>
          <p className="text-gray-600 mb-6">
            You're registered for <strong>{event?.title}</strong>. We'll send a
            confirmation to <strong>{email}</strong>.
          </p>
          {event?.start_date && (
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <div className="flex items-center gap-2 text-gray-700">
                <CalendarIcon className="h-5 w-5" />
                <span>
                  {new Date(event.start_date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              {event.start_date && (
                <div className="mt-2 text-gray-600 pl-7">
                  {new Date(event.start_date).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              )}
              {event.location_name && (
                <div className="flex items-center gap-2 text-gray-700 mt-3">
                  <MapPinIcon className="h-5 w-5" />
                  <span>{event.location_name}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const isFull =
    regClosed ||
    (event?.capacity &&
      event.current_attendee_count !== undefined &&
      event.current_attendee_count >= event.capacity);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Event Info Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-primary-600 px-6 py-4">
            <h1 className="text-xl font-bold text-white">{event?.title}</h1>
          </div>
          <div className="p-6 space-y-3">
            {event?.start_date && (
              <div className="flex items-center gap-3 text-gray-700">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div>
                    {new Date(event.start_date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(event.start_date).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            )}
            {event?.location_name && (
              <div className="flex items-center gap-3 text-gray-700">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div>{event.location_name}</div>
                  {event.location_address && (
                    <div className="text-sm text-gray-500">
                      {event.location_address.street &&
                        `${event.location_address.street}, `}
                      {event.location_address.city &&
                        `${event.location_address.city}, `}
                      {event.location_address.state}
                    </div>
                  )}
                </div>
              </div>
            )}
            {event?.description && (
              <p className="text-gray-600 text-sm pt-2 border-t border-gray-100">
                {event.description}
              </p>
            )}
          </div>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <UserPlusIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Register for Event
              </h2>
              <p className="text-sm text-gray-500">
                Fill in your details to reserve your spot
              </p>
            </div>
          </div>

          {isFull ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
              <h3 className="font-medium text-yellow-800">Event at Capacity</h3>
              <p className="text-sm text-yellow-700 mt-1">
                This event has reached its maximum capacity. Please check back
                later or contact the organizer.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Registering...
                  </span>
                ) : (
                  "Complete Registration"
                )}
              </button>
            </form>
          )}
        </div>

        {/* Capacity indicator */}
        {event?.capacity &&
          event.current_attendee_count !== undefined &&
          !isFull && (
            <div className="mt-4 text-center text-sm text-gray-500">
              {event.capacity - event.current_attendee_count} spots remaining
            </div>
          )}
      </div>
    </div>
  );
}
